import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  LabelTemplateDto,
  LabelTemplateEditDto
} from 'src/store/labelTemplates/labelTemplate';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum LabelTemplateTypes {
  FetchAll = 'labelTemplates/FetchAll',
  Add = 'labelTemplates/Add',
  Update = 'labelTemplates/Update',
  Delete = 'labelTemplates/Delete',
  Failure = 'labelTemplates/Failure'
}

export type LabelTemplateAction =
  | { type: LabelTemplateTypes.FetchAll; labelTemplates: LabelTemplateDto[] }
  | { type: LabelTemplateTypes.Add; labelTemplate: LabelTemplateDto }
  | { type: LabelTemplateTypes.Update; labelTemplate: LabelTemplateDto }
  | { type: LabelTemplateTypes.Delete; labelTemplate: LabelTemplateDto }
  | { type: LabelTemplateTypes.Failure; errors: any };

export const actionCreators = {
  fetchLabelTemplates: (
    labelTemplates: LabelTemplateDto[]
  ): LabelTemplateAction => ({
    type: LabelTemplateTypes.FetchAll,
    labelTemplates
  }),
  addLabelTemplate: (labelTemplate: LabelTemplateDto): LabelTemplateAction => ({
    type: LabelTemplateTypes.Add,
    labelTemplate
  }),
  updateLabelTemplate: (
    labelTemplate: LabelTemplateDto
  ): LabelTemplateAction => ({
    type: LabelTemplateTypes.Update,
    labelTemplate
  }),
  deleteLabelTemplate: (
    labelTemplate: LabelTemplateDto
  ): LabelTemplateAction => ({
    type: LabelTemplateTypes.Delete,
    labelTemplate
  }),
  labelTemplateRequestFailure: (errors: any): LabelTemplateAction => ({
    type: LabelTemplateTypes.Failure,
    errors
  })
};

export const fetchLabelTemplates = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(LabelTemplateTypes.FetchAll));
    const { ok, data, error } = await getRequest<LabelTemplateDto[]>(
      '/labelTemplates'
    );
    dispatch(requestEnd(LabelTemplateTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchLabelTemplates(data))
      : dispatch(actionCreators.labelTemplateRequestFailure(error));
  };
};

export const createLabelTemplate = (labelTemplate: LabelTemplateEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/labelTemplates',
      labelTemplate
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('labelTemplate.created')));
      return dispatch(
        actionCreators.addLabelTemplate({ ...labelTemplate, id: data })
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.labelTemplateRequestFailure(error));
  };
};

export const editLabelTemplate = (
  id: number,
  labelTemplate: LabelTemplateEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(
      `/labelTemplates/${id}`,
      labelTemplate
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('labelTemplate.updated')));
      return dispatch(
        actionCreators.updateLabelTemplate(labelTemplate as LabelTemplateDto)
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.labelTemplateRequestFailure(error));
  };
};

export const deleteLabelTemplate = (
  id: number,
  labelTemplate: LabelTemplateDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(
      `/labelTemplates/${id}`,
      labelTemplate
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('labelTemplate.deleted')));
      return dispatch(actionCreators.deleteLabelTemplate(labelTemplate));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.labelTemplateRequestFailure(error));
  };
};
